import React, {Component, PropTypes} from 'react';
import {OnboardingDelayWithTransitions, OnboardingDelayWithoutTransitions} from 'constants/app'
import './onboarding.scss';

export default class Onboarding extends Component {

  static propTypes = {
    children: PropTypes.node
  };

  static contextTypes = {
    profile: PropTypes.object.isRequired,
    updateOnboardingState: PropTypes.func.isRequired,
    userId: PropTypes.string.isRequired,
    showHUD: PropTypes.func.isRequired,
    hideHUD: PropTypes.func.isRequired,
    setMode: PropTypes.func.isRequired,
    deckState: PropTypes.object.isRequired,
    i18n: PropTypes.func.isRequired
  };

  constructor(props, context) {
    super(props, context);
    this.state = {step: 0, wait: false};
    const { i18n: _ } = this.context;

    this.steps = [
      {
        title: _('Welcome Training Agent!'),
        text: _('Let\'s spend 30 seconds orienting you to the platform!'),
        delayAfter: OnboardingDelayWithoutTransitions
      },
      {
        highlight: 'Menu',
        attachmentPoint: [1, 0.5],
        text: _('Click on MENU to open up the Heads Up Display menu.'),
        action: context.showHUD,
        delayAfter: OnboardingDelayWithTransitions
      },
      {
        highlight: 'Scores',
        attachmentPoint: [0.5, 1],
        text: _('Click on SCORES to check out the Scoreboard. Your class will be divided into four teams and will compete for points!'),
        action: () => context.setMode('scores'),
        delayAfter: OnboardingDelayWithTransitions
      },
      {
        highlight: 'Extras',
        attachmentPoint: [0.5, 0],
        text: _('Click on EXTRAS for bonus resources for more learning and laughter!'),
        action: () => context.setMode('extras'),
        delayAfter: OnboardingDelayWithoutTransitions
      },
      {
        highlight: 'Modules',
        attachmentPoint: [0.5, 0],
        text: _('Click on MODULES for an overview of all the levels in the program. You\'ll unlock levels as you progress!'),
        action: () => context.setMode('modules'),
        delayAfter: OnboardingDelayWithoutTransitions
      },
      {
        highlight: 'Exit',
        attachmentPoint: [0.5, 0],
        text: _('Click on EXIT to leave the presentation and go back to the DASHBOARD page any time.'),
        action: () => context.setMode('exit'),
        delayAfter: OnboardingDelayWithoutTransitions
      },
      {
        highlight: 'Hide',
        attachmentPoint: [0.5, 0],
        text: _('Click HIDE to close the Heads Up Display and get back to the action!'),
        action: () => {
          context.setMode('')
            .then(() => context.showHUD());
        },
        delayAfter: OnboardingDelayWithTransitions
      },
      {
        title: _('Almost there!'),
        highlight: 'Next Arrow',
        attachmentPoint: [0, 0.5],
        text: (
          <p>
            {_('You can progress through the slides in 3 ways:')}
            <ol>
              <li>{_('Use the arrow(s) as highlighted')}</li>
              <li>{_('Use the left/right keys on your keyboard')}</li>
              <li>{_('Use your Remote Control if you have it set up!')}</li>
            </ol>
          </p>
        )
      }
    ];
  }

  componentDidMount() {
    const {step} = this.state;
    const {hideHUD} = this.context;
    if (step === 0) {
      hideHUD();
    }
  }

  nextStep(evt) {
    evt.stopPropagation();
    evt.preventDefault();
    const {step} = this.state;
    if (step === this.steps.length - 1) {
      return this.done(evt);
    }
    if (this.steps[step].action) {
      this.steps[step].action();
    }
    this.setState({step: step + 1, wait: true});
    const self = this;
    setTimeout(() => {
      self.setState({wait: false});
    }, this.steps[step].delayAfter || 0);
  }

  skipOnboarding(evt) {
    evt.stopPropagation();
    evt.preventDefault();
    const {userId, updateOnboardingState, sessionId} = this.context;
    updateOnboardingState(userId, 'dismissed', sessionId);
  }

  done(evt) {
    evt.stopPropagation();
    evt.preventDefault();
    const {userId, updateOnboardingState, sessionId} = this.context;
    updateOnboardingState(userId, 'completed', sessionId);
  }

  _generateStep() {
    const {wait, step} = this.state;
    if (wait) return null;
    const {deckState: {refs: deckRefs}, i18n: _} = this.context;
    const stepData = this.steps[step];
    const {highlight, attachmentPoint} = stepData;
    let hlTop = 0;
    let hlLeft = 0;
    let hlXRadius = 0;
    let hlYRadius = 0;
    if (highlight && deckRefs && deckRefs[highlight]) {
      const highlightRef = deckRefs[highlight];
      const boundingClientRect = highlightRef.getBoundingClientRect();
      hlTop = boundingClientRect.top - 5;
      hlLeft = boundingClientRect.left - 5;
      hlXRadius = (boundingClientRect.width / 2) + 5;
      hlYRadius = (boundingClientRect.height / 2) + 5;
    }
    const text = typeof stepData.text === 'string'
      ? stepData.text.split('\n').map((paragraph, index) => (<p key={index}>{paragraph}</p>))
      : stepData.text;
    const emptyTitle = stepData.title ? '' : ' empty-title';
    return (
      <div className="spectacle-deck">
        {highlight &&
        <svg className={`pointer step-${step}`} width="100%" height="100%">
          <line x1="50%" y1="50%" x2={hlLeft + (hlXRadius * 2 * attachmentPoint[0])}
                y2={hlTop + (hlYRadius * 2 * attachmentPoint[1])} strokeWidth={2}/>
          <ellipse cx={hlLeft + hlXRadius} cy={hlTop + hlYRadius} rx={hlXRadius} ry={hlYRadius} strokeWidth={2}
                   fillOpacity={0.25} onClick={::this.nextStep}/>
        </svg>
        }
        <div className="modal-dialog onboarding-modal" ref="box">
          <div className="modal-content">
            <div className={`modal-header${emptyTitle}`}>
              <h4 className="modal-title">{stepData.title}</h4>
            </div>
            <div className="modal-body">
              {text}
              {step === 0 &&
              <p>
                <button className="btn btn-primary" onClick={::this.nextStep}>{_('Let\'s Go!')}</button>
                &nbsp;
                <button className="btn btn-default" onClick={::this.skipOnboarding}>{_('No Thanks')}</button>
              </p>
              }
              {step === this.steps.length - 1 &&
              <p>
                <a href="#" onClick={::this.nextStep}>{_('Great, I\'m all trained up!')}</a>
              </p>
              }
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        <div className="onboarding">
          {this._generateStep()}
        </div>
        {this.props.children}
      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./src/views/session/presentation/components/onboarding.jsx